/**
 * Per-line reveal. Splits a `.line-reveal` element's text into its visual lines
 * and slides each line up from behind its own mask, staggered, when the element
 * scrolls into view. Add `line-reveal` to any text block to opt in.
 *
 * Progressive enhancement: the markup renders normally server-side, and the
 * split only happens if this runs — so with JS off, or under reduced motion,
 * the text is simply there. Re-splits on resize, since where the lines break
 * depends on the width.
 */
let started = false;

export function initLineReveal() {
	if ( started || typeof window === 'undefined' ) return;
	if ( ! ( 'IntersectionObserver' in window ) ) return;
	if ( window.matchMedia?.( '(prefers-reduced-motion: reduce)' ).matches ) return;
	started = true;

	const io = new IntersectionObserver(
		( entries ) => {
			entries.forEach( ( entry ) => {
				if ( entry.isIntersecting ) {
					entry.target.classList.add( 'is-revealed' );
					io.unobserve( entry.target );
				}
			} );
		},
		{ threshold: 0, rootMargin: '0px 0px -20% 0px' }
	);

	// Elements that host the lines: block-level children with text, or the
	// element itself when it holds only text.
	const pickHosts = ( el ) => {
		const blocks = Array.from( el.children ).filter(
			( c ) =>
				/^(P|H[1-6]|LI|DIV|BLOCKQUOTE|FIGCAPTION)$/.test( c.tagName ) &&
				c.textContent.trim()
		);
		return blocks.length ? blocks : [ el ];
	};

	// Wrap each word in an inline span, recursing through inline elements (<em>,
	// <a>, …) so their structure and the wrapping are untouched — this is what
	// lets us measure where the visual lines break.
	const wrapWords = ( node ) => {
		Array.from( node.childNodes ).forEach( ( child ) => {
			if ( child.nodeType === 3 ) {
				const frag = document.createDocumentFragment();
				child.textContent.split( /(\s+)/ ).forEach( ( part ) => {
					if ( ! part ) return;
					if ( /^\s+$/.test( part ) ) {
						frag.appendChild( document.createTextNode( part ) );
						return;
					}
					const word = document.createElement( 'span' );
					word.className = 'lr-w';
					word.textContent = part;
					frag.appendChild( word );
				} );
				child.replaceWith( frag );
			} else if ( child.nodeType === 1 ) {
				wrapWords( child );
			}
		} );
	};

	// Split a host into masked lines starting at global index `from`; returns the
	// next index. Inline markup survives: a line crossing an <em>/<a> gets that
	// element split across the two lines by Range.extractContents().
	const splitHost = ( host, from ) => {
		wrapWords( host );
		const words = Array.from( host.querySelectorAll( '.lr-w' ) );
		if ( ! words.length ) return from;

		// Group words into visual lines by their measured top.
		const groups = [];
		let top = null;
		words.forEach( ( word ) => {
			const t = word.getBoundingClientRect().top;
			if ( top === null || t - top > 2 ) {
				groups.push( { first: word, last: word } );
				top = t;
			} else {
				groups[ groups.length - 1 ].last = word;
			}
		} );

		const lines = groups.map( ( group, i ) => {
			const range = document.createRange();
			range.setStartBefore( group.first );
			range.setEndAfter( group.last );
			const inner = document.createElement( 'span' );
			inner.className = 'line__i';
			inner.style.setProperty( '--line', String( from + i ) );
			inner.appendChild( range.extractContents() );
			// The whitespace at the break sits between two lines and is lost, so
			// the text would read "promessede". Trailing space on a nowrap line
			// hangs, so it costs nothing visually.
			if ( i < groups.length - 1 ) {
				inner.appendChild( document.createTextNode( ' ' ) );
			}
			const line = document.createElement( 'span' );
			line.className = 'line';
			line.appendChild( inner );
			return line;
		} );
		host.replaceChildren( ...lines );
		return from + groups.length;
	};

	const splitElement = ( el ) => {
		const hosts = pickHosts( el );
		let index = 0;
		hosts.forEach( ( host ) => {
			if ( host.lineRevealSource === undefined ) {
				host.lineRevealSource = host.innerHTML;
			}
			// Restore the pristine markup so a re-split measures cleanly.
			host.innerHTML = host.lineRevealSource;
			index = splitHost( host, index );
		} );
		return hosts;
	};

	const setup = ( el ) => {
		if ( el.lineRevealHosts ) return;
		el.lineRevealHosts = splitElement( el );
		io.observe( el );
	};

	// Measure only once the webfonts are ready: line breaks differ between the
	// fallback and the real font, so splitting early mis-fits the (nowrap) masks.
	const start = () => document.querySelectorAll( '.line-reveal' ).forEach( setup );
	if ( document.fonts?.ready ) {
		document.fonts.ready.then( start );
	} else {
		start();
	}

	// Where lines break depends on width, so re-split on resize. Already-revealed
	// elements re-split without replaying the animation.
	let resizeTimer = null;
	window.addEventListener( 'resize', () => {
		clearTimeout( resizeTimer );
		resizeTimer = setTimeout( () => {
			document.querySelectorAll( '.line-reveal' ).forEach( ( el ) => {
				if ( ! el.lineRevealHosts ) return;
				const revealed = el.classList.contains( 'is-revealed' );
				if ( revealed ) el.classList.add( 'is-instant' );
				el.lineRevealHosts = splitElement( el );
				if ( revealed ) {
					requestAnimationFrame( () => el.classList.remove( 'is-instant' ) );
				}
			} );
		}, 150 );
	} );
}
